import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthState, AuthTokens, LoginCredentials, MfaVerifyData, RegisterData, User } from '../types';
import { authApi } from '../services/auth-api';
import {
  clearTokens,
  getAccessToken,
  setTokenRefreshCallbacks,
  setTokens,
} from '../services/token-manager';

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  verifyMfa: (data: MfaVerifyData) => Promise<void>;
  setAuthState: (user: User, tokens: AuthTokens) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: true,
    mfaPending: false,
  });

  // Setup token refresh callbacks
  useEffect(() => {
    setTokenRefreshCallbacks(
      (newTokens) => {
        setState((prev) => ({ ...prev, tokens: newTokens }));
      },
      () => {
        setState({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          mfaPending: false,
        });
        clearTokens();
      },
    );
  }, []);

  // Try to restore session on mount - first try access token, then refresh
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('gp_refresh_token');

    const tryRestore = async () => {
      // First: try the stored access token directly
      if (storedAccessToken) {
        try {
          const user = await authApi.getMe(storedAccessToken);
          const restoredTokens = { accessToken: storedAccessToken, refreshToken: storedRefreshToken || '', expiresIn: 900 };
          setTokens(restoredTokens);
          setState({ user, tokens: restoredTokens, isAuthenticated: true, isLoading: false, mfaPending: false });
          return;
        } catch {
          // Access token expired, try refresh below
        }
      }

      // Second: try refresh token
      if (storedRefreshToken) {
        try {
          const tokens = await authApi.refreshToken(storedRefreshToken);
          setTokens(tokens);
          const user = await authApi.getMe(tokens.accessToken);
          setState({ user, tokens, isAuthenticated: true, isLoading: false, mfaPending: false });
          localStorage.setItem('gp_refresh_token', tokens.refreshToken);
          return;
        } catch {
          localStorage.removeItem('gp_refresh_token');
          localStorage.removeItem('accessToken');
        }
      }

      // No valid session
      setState((prev) => ({ ...prev, isLoading: false }));
    };

    tryRestore();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);

    if (response.mfaRequired && response.sessionToken) {
      setState((prev) => ({
        ...prev,
        mfaPending: true,
        tokens: { accessToken: '', refreshToken: '', expiresIn: 0 },
      }));
      // Store sessionToken temporarily for MFA verification
      localStorage.setItem('gp_mfa_session', response.sessionToken);
      return;
    }

    if (response.accessToken && response.refreshToken) {
      const tokens: AuthTokens = {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
      };
      setTokens(tokens);
      const user = response.user || (await authApi.getMe(tokens.accessToken));
      localStorage.setItem('gp_refresh_token', tokens.refreshToken);
      setState({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        mfaPending: false,
      });
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    await authApi.register(data);
  }, []);

  const logout = useCallback(async () => {
    const accessToken = getAccessToken();
    const refreshToken = state.tokens?.refreshToken;
    if (accessToken && refreshToken) {
      try {
        await authApi.logout(accessToken, refreshToken);
      } catch {
        // Proceed with local logout even if server logout fails
      }
    }
    clearTokens();
    localStorage.removeItem('gp_refresh_token');
    localStorage.removeItem('gp_mfa_session');
    setState({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      mfaPending: false,
    });
  }, [state.tokens]);

  const verifyMfa = useCallback(async (data: MfaVerifyData) => {
    const response = await authApi.verifyMfa(data);
    const tokens: AuthTokens = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
    };
    setTokens(tokens);
    localStorage.setItem('gp_refresh_token', tokens.refreshToken);
    localStorage.removeItem('gp_mfa_session');
    setState({
      user: response.user,
      tokens,
      isAuthenticated: true,
      isLoading: false,
      mfaPending: false,
    });
  }, []);

  const setAuthState = useCallback((user: User, tokens: AuthTokens) => {
    setTokens(tokens);
    localStorage.setItem('gp_refresh_token', tokens.refreshToken);
    setState({
      user,
      tokens,
      isAuthenticated: true,
      isLoading: false,
      mfaPending: false,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      register,
      logout,
      verifyMfa,
      setAuthState,
    }),
    [state, login, register, logout, verifyMfa, setAuthState],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


